import { computed, onBeforeUnmount, ref, watch, type ComputedRef, type Ref } from 'vue'
import { useApiMonOrga } from '~/composables/api/useApiMonOrga'
import { useEnedisSitesMapModal } from '~/composables/sites/useEnedisSitesMapModal'
import { useSourceToast } from '~/composables/ui/useSourceToast'
import {
  SEARCH_ENTITY_DISPLAY_ORDER,
  SEARCH_ENTITY_TYPE_LABEL,
  SEARCH_ENTITY_TYPES_ORDERED,
  type SearchEntityType,
} from '~/constants/searchEntityMeta'
import type {
  GlobalSearchResultGroup,
  GlobalSearchStatus,
  SearchApiResponse,
  SearchApiResultItem,
  SearchFeedbackPayload,
  SearchFeedbackSentiment,
} from '~/types/search'

const GLOBAL_SEARCH_DEBOUNCE_MS = 300
const GLOBAL_SEARCH_SCOPE_DEBOUNCE_MS = 180
const GLOBAL_SEARCH_MIN_QUERY_LENGTH = 2

type ExecuteSearchOptions = { scopeOnly?: boolean }

export type GlobalSearchRuntime = {
  toast: ReturnType<typeof useSourceToast>
  query: Ref<string>
  results: Ref<SearchApiResultItem[]>
  stats: Ref<SearchApiResponse['stats'] | null>
  status: Ref<GlobalSearchStatus>
  scopeTypes: Ref<SearchEntityType[]>
  scopeRefetching: Ref<boolean>
  panelOpen: Ref<boolean>
  currentQueryId: Ref<string | null>
  feedbackDismissed: Ref<boolean>
  debounceTimer: ReturnType<typeof setTimeout> | null
  scopeDebounceTimer: ReturnType<typeof setTimeout> | null
  requestSequence: number
  cachedGlobalResults: SearchApiResultItem[]
  cachedGlobalQuery: string
  lastFeedbackQuery: string
}

type GlobalSearchView = {
  trimmedQuery: ComputedRef<string>
  hasMinimumQuery: ComputedRef<boolean>
  topResults: ComputedRef<SearchApiResultItem[]>
  flatSelectableResults: ComputedRef<SearchApiResultItem[]>
}

function readSitLibFromSearchResult(resultItem: SearchApiResultItem): string | null {
  const payloadSitLib = typeof resultItem.payload.sitLib === 'string' ? resultItem.payload.sitLib.trim() : ''
  if (payloadSitLib !== '') return payloadSitLib
  const titleSitLib = resultItem.title.trim()
  return titleSitLib !== '' ? titleSitLib : null
}

export function createGlobalSearchRuntime(): GlobalSearchRuntime {
  return {
    toast: useSourceToast(),
    query: ref(''),
    results: ref<SearchApiResultItem[]>([]),
    stats: ref<SearchApiResponse['stats'] | null>(null),
    status: ref<GlobalSearchStatus>('idle'),
    scopeTypes: ref<SearchEntityType[]>([]),
    scopeRefetching: ref(false),
    panelOpen: ref(false),
    currentQueryId: ref<string | null>(null),
    feedbackDismissed: ref(false),
    debounceTimer: null,
    scopeDebounceTimer: null,
    requestSequence: 0,
    cachedGlobalResults: [],
    cachedGlobalQuery: '',
    lastFeedbackQuery: '',
  }
}

function groupedSearchResults(runtime: GlobalSearchRuntime) {
  return computed<GlobalSearchResultGroup[]>(() => {
    const groupsByType = new Map<SearchEntityType, SearchApiResultItem[]>()
    for (const resultItem of runtime.results.value) {
      const existingGroup = groupsByType.get(resultItem.type)
      if (existingGroup) existingGroup.push(resultItem)
      else groupsByType.set(resultItem.type, [resultItem])
    }
    const groups: GlobalSearchResultGroup[] = []
    for (const [entityType, groupItems] of groupsByType.entries()) {
      groups.push({
        type: entityType,
        typeLabel: SEARCH_ENTITY_TYPE_LABEL[entityType] ?? entityType,
        items: [...groupItems].sort((leftItem, rightItem) => rightItem.score - leftItem.score),
      })
    }
    groups.sort((leftGroup, rightGroup) =>
      SEARCH_ENTITY_DISPLAY_ORDER[leftGroup.type] - SEARCH_ENTITY_DISPLAY_ORDER[rightGroup.type])
    return groups
  })
}

export function buildGlobalSearchView(runtime: GlobalSearchRuntime) {
  const trimmedQuery = computed(() => runtime.query.value.trim())
  const hasMinimumQuery = computed(() => trimmedQuery.value.length >= GLOBAL_SEARCH_MIN_QUERY_LENGTH)
  const resultFilterTypes = computed<SearchEntityType[]>(() => {
    const sourceResults = runtime.cachedGlobalResults.length > 0 ? runtime.cachedGlobalResults : runtime.results.value
    const typesPresent = new Set<SearchEntityType>()
    for (const resultItem of sourceResults) typesPresent.add(resultItem.type)
    return SEARCH_ENTITY_TYPES_ORDERED.filter((entityType) => typesPresent.has(entityType))
  })
  const topResults = computed<SearchApiResultItem[]>(() =>
    [...runtime.results.value].sort((leftItem, rightItem) => rightItem.score - leftItem.score))
  return {
    trimmedQuery,
    hasMinimumQuery,
    availableScopeTypes: SEARCH_ENTITY_TYPES_ORDERED,
    resultFilterTypes,
    groupedResults: groupedSearchResults(runtime),
    topResults,
    flatSelectableResults: computed(() => topResults.value),
    showFeedbackPrompt: computed(() => runtime.status.value === 'ready' || runtime.status.value === 'empty'),
    feedbackAlreadySubmitted: computed(() => runtime.feedbackDismissed.value),
  }
}

function clearDebounceTimer(runtime: GlobalSearchRuntime): void {
  if (runtime.debounceTimer !== null) {
    clearTimeout(runtime.debounceTimer)
    runtime.debounceTimer = null
  }
}

function clearScopeDebounceTimer(runtime: GlobalSearchRuntime): void {
  if (runtime.scopeDebounceTimer !== null) {
    clearTimeout(runtime.scopeDebounceTimer)
    runtime.scopeDebounceTimer = null
  }
}

function resetSearchState(runtime: GlobalSearchRuntime): void {
  runtime.results.value = []
  runtime.stats.value = null
  runtime.status.value = 'idle'
  runtime.currentQueryId.value = null
  runtime.feedbackDismissed.value = false
  runtime.lastFeedbackQuery = ''
  runtime.cachedGlobalResults = []
  runtime.cachedGlobalQuery = ''
}

function closePanel(runtime: GlobalSearchRuntime): void {
  runtime.panelOpen.value = false
}

function openPanel(runtime: GlobalSearchRuntime, hasMinimumQuery: boolean): void {
  if (hasMinimumQuery) runtime.panelOpen.value = true
}

function filterResultsByScopeTypes(
  sourceResults: SearchApiResultItem[],
  activeScopeTypes: SearchEntityType[],
): SearchApiResultItem[] {
  if (activeScopeTypes.length === 0) return sourceResults
  const activeScopeSet = new Set(activeScopeTypes)
  return sourceResults.filter((resultItem) => activeScopeSet.has(resultItem.type))
}

function applyOptimisticScopePreview(runtime: GlobalSearchRuntime, trimmedQuery: string): void {
  if (trimmedQuery.length < GLOBAL_SEARCH_MIN_QUERY_LENGTH) return
  if (runtime.cachedGlobalQuery !== trimmedQuery || runtime.cachedGlobalResults.length === 0) return
  const previewResults = filterResultsByScopeTypes(runtime.cachedGlobalResults, runtime.scopeTypes.value)
  runtime.results.value = previewResults
  runtime.status.value = previewResults.length > 0 ? 'ready' : 'empty'
}

function syncFeedbackPromptForQuery(runtime: GlobalSearchRuntime, normalizedQuery: string, isScopeOnlyRefetch: boolean): void {
  if (isScopeOnlyRefetch && normalizedQuery === runtime.lastFeedbackQuery) return
  if (normalizedQuery !== runtime.lastFeedbackQuery) {
    runtime.feedbackDismissed.value = false
    runtime.lastFeedbackQuery = normalizedQuery
  }
}

function publishSearchResponse(
  runtime: GlobalSearchRuntime,
  response: SearchApiResponse,
  normalizedQuery: string,
  isScopeOnlyRefetch: boolean,
): void {
  runtime.results.value = response.results
  runtime.stats.value = response.stats
  runtime.currentQueryId.value = response.queryId
  syncFeedbackPromptForQuery(runtime, normalizedQuery, isScopeOnlyRefetch)
  if (runtime.scopeTypes.value.length === 0) {
    runtime.cachedGlobalResults = response.results
    runtime.cachedGlobalQuery = normalizedQuery
  }
  runtime.status.value = response.results.length === 0 ? 'empty' : 'ready'
}

function reportSearchFailure(runtime: GlobalSearchRuntime, searchError: unknown, isScopeOnlyRefetch: boolean): void {
  if (!isScopeOnlyRefetch) {
    runtime.results.value = []
    runtime.stats.value = null
    runtime.status.value = 'error'
    runtime.panelOpen.value = true
  }
  const errorMessage = searchError instanceof Error && searchError.message.trim().length > 0
    ? searchError.message
    : 'La recherche globale est momentanément indisponible.'
  runtime.toast.error('Recherche globale', errorMessage)
}

async function executeSearch(
  runtime: GlobalSearchRuntime,
  searchQuery: string,
  options: ExecuteSearchOptions = {},
): Promise<void> {
  const normalizedQuery = searchQuery.trim()
  if (normalizedQuery.length < GLOBAL_SEARCH_MIN_QUERY_LENGTH) {
    resetSearchState(runtime)
    closePanel(runtime)
    return
  }
  runtime.requestSequence += 1
  const currentRequest = runtime.requestSequence
  const isScopeOnlyRefetch = options.scopeOnly === true
  if (isScopeOnlyRefetch) runtime.scopeRefetching.value = true
  else {
    runtime.status.value = 'loading'
    runtime.panelOpen.value = true
  }
  const requestParams: Record<string, string> = { q: normalizedQuery }
  if (runtime.scopeTypes.value.length > 0) requestParams.types = runtime.scopeTypes.value.join(',')
  try {
    const response = await useApiMonOrga<SearchApiResponse>('GET', '/v1/search', requestParams)
    if (currentRequest !== runtime.requestSequence) return
    publishSearchResponse(runtime, response, normalizedQuery, isScopeOnlyRefetch)
  } catch (searchError: unknown) {
    if (currentRequest !== runtime.requestSequence) return
    reportSearchFailure(runtime, searchError, isScopeOnlyRefetch)
  } finally {
    if (currentRequest === runtime.requestSequence) runtime.scopeRefetching.value = false
  }
}

function scheduleSearch(runtime: GlobalSearchRuntime, view: GlobalSearchView): void {
  clearDebounceTimer(runtime)
  clearScopeDebounceTimer(runtime)
  if (!view.hasMinimumQuery.value) {
    resetSearchState(runtime)
    closePanel(runtime)
    return
  }
  runtime.status.value = 'loading'
  runtime.panelOpen.value = true
  runtime.debounceTimer = setTimeout(() => {
    runtime.debounceTimer = null
    void executeSearch(runtime, view.trimmedQuery.value)
  }, GLOBAL_SEARCH_DEBOUNCE_MS)
}

function scheduleScopeSearch(runtime: GlobalSearchRuntime, view: GlobalSearchView): void {
  clearScopeDebounceTimer(runtime)
  clearDebounceTimer(runtime)
  if (!view.hasMinimumQuery.value) return
  runtime.scopeDebounceTimer = setTimeout(() => {
    runtime.scopeDebounceTimer = null
    void executeSearch(runtime, view.trimmedQuery.value, { scopeOnly: true })
  }, GLOBAL_SEARCH_SCOPE_DEBOUNCE_MS)
}

function recordResultClick(runtime: GlobalSearchRuntime, resultItem: SearchApiResultItem, flatResults: SearchApiResultItem[]): void {
  const position = flatResults.findIndex((candidate) => candidate.documentId === resultItem.documentId)
  void useApiMonOrga('POST', '/v1/search/click', undefined, {
    queryId: runtime.currentQueryId.value,
    entityType: resultItem.type,
    documentId: resultItem.documentId,
    entityKey: resultItem.payload,
    position: position >= 0 ? position : 0,
    score: resultItem.score,
    matchedFields: resultItem.matchedFields,
  }).catch(() => undefined)
}

function submitFeedback(
  runtime: GlobalSearchRuntime,
  sentiment: SearchFeedbackSentiment,
  topResults: SearchApiResultItem[],
  trimmedQuery: string,
  comment?: string,
): void {
  if (runtime.feedbackDismissed.value) return
  runtime.feedbackDismissed.value = true
  const feedbackPayload: SearchFeedbackPayload = {
    queryId: runtime.currentQueryId.value,
    sentiment,
    comment: comment ?? null,
    resultSnapshot: {
      returnedCount: runtime.results.value.length,
      topDocumentIds: topResults.slice(0, 10).map((resultItem) => resultItem.documentId),
      queryNormalized: trimmedQuery.length > 0 ? trimmedQuery.toLowerCase() : null,
    },
  }
  void useApiMonOrga('POST', '/v1/search/feedback', undefined, feedbackPayload).catch(() => undefined)
  runtime.toast.success(
    'Recherche',
    sentiment === 'helpful' ? 'Merci pour votre retour.' : 'Votre retour a bien été enregistré.',
  )
}

async function selectResult(
  runtime: GlobalSearchRuntime,
  resultItem: SearchApiResultItem,
  flatResults: SearchApiResultItem[],
): Promise<void> {
  const destinationPath = resultItem.routePath
  recordResultClick(runtime, resultItem, flatResults)
  clearDebounceTimer(runtime)
  clearScopeDebounceTimer(runtime)
  runtime.requestSequence += 1
  runtime.query.value = ''
  runtime.scopeTypes.value = []
  runtime.scopeRefetching.value = false
  resetSearchState(runtime)
  closePanel(runtime)
  if (resultItem.type === 'site') {
    const sitLib = readSitLibFromSearchResult(resultItem)
    if (sitLib) useEnedisSitesMapModal().openWithSite(sitLib)
    return
  }
  if (destinationPath && destinationPath.trim().length > 0) await navigateTo(destinationPath)
}

export function bindGlobalSearchActions(runtime: GlobalSearchRuntime, view: GlobalSearchView & {
  topResults: ComputedRef<SearchApiResultItem[]>
  flatSelectableResults: ComputedRef<SearchApiResultItem[]>
}) {
  watch(runtime.query, () => {
    clearScopeDebounceTimer(runtime)
    runtime.cachedGlobalResults = []
    runtime.cachedGlobalQuery = ''
    scheduleSearch(runtime, view)
  })
  watch(runtime.scopeTypes, () => {
    if (!view.hasMinimumQuery.value) return
    applyOptimisticScopePreview(runtime, view.trimmedQuery.value)
    scheduleScopeSearch(runtime, view)
  }, { deep: true })
  onBeforeUnmount(() => {
    clearDebounceTimer(runtime)
    clearScopeDebounceTimer(runtime)
    runtime.requestSequence += 1
  })
  return {
    openPanel: () => openPanel(runtime, view.hasMinimumQuery.value),
    closePanel: () => closePanel(runtime),
    resetSearchState: () => resetSearchState(runtime),
    toggleScopeType: (entityType: SearchEntityType) => {
      const currentScope = runtime.scopeTypes.value
      runtime.scopeTypes.value = currentScope.includes(entityType)
        ? currentScope.filter((scopeType) => scopeType !== entityType)
        : [...currentScope, entityType]
    },
    clearScopeTypes: () => {
      if (runtime.scopeTypes.value.length === 0) return
      runtime.scopeTypes.value = []
    },
    clearQuery: () => {
      clearDebounceTimer(runtime)
      clearScopeDebounceTimer(runtime)
      runtime.requestSequence += 1
      runtime.query.value = ''
      runtime.scopeRefetching.value = false
      resetSearchState(runtime)
      closePanel(runtime)
    },
    isScopeTypeActive: (entityType: SearchEntityType) => runtime.scopeTypes.value.includes(entityType),
    selectResult: (resultItem: SearchApiResultItem) =>
      selectResult(runtime, resultItem, view.flatSelectableResults.value),
    submitFeedback: (sentiment: SearchFeedbackSentiment, comment?: string) =>
      submitFeedback(runtime, sentiment, view.topResults.value, view.trimmedQuery.value, comment),
    scheduleSearch: () => scheduleSearch(runtime, view),
  }
}
